import { create, type StateCreator } from 'zustand'
import { useMainStore } from '@/stores/main'
import { useDevicesStore } from '@/stores/devices'
import UAParser from 'ua-parser-js'

// Types
import type { UAParserInstance, IResult } from 'ua-parser-js'
import type { AddressId, ChannelType } from '@/helpers/types'
import { CHANNEL_TYPE } from '@/helpers/constants'
import { userToColor } from '@/helpers/utils'

interface GetUserVariablesParams {
  channel?: ChannelType | undefined
}

interface UserMetaDataRecord {
  color: string
  id: AddressId
  initial: string
  name: string
}

type UserMetaDataMap = Map<AddressId, UserMetaDataRecord>

interface UserVariables {
  cameraLabel: string
  company: string
  country: string
  displayName: string
  email: string
  firstName: string
  fullBrowserVersion: string
  gmtOffset: number
  hostname: string
  isAndroid: boolean
  isChrome: boolean
  isChromium: boolean
  isEdge: boolean
  isFirefox: boolean
  isIE: boolean
  isIOS: boolean
  isMobile: boolean
  isOpera: boolean
  isSafari: boolean
  isTablet: boolean
  isWinPhone: boolean
  isYandex: boolean
  lastName: string
  memberId: string
  microphoneLabel: string
  name: string
  osName: string
  osVersion: string
  timezone: string
  tzString: string
  userAgent: string
}

interface Actions {
  actions: {
    _getDisplayNameFallback: (
      params: Partial<{
        email: string
        firstName: string
        lastName: string
      }>,
    ) => string
    getUserVariables: (
      params?: GetUserVariablesParams,
    ) => Promise<UserVariables>
    refreshCameraLabel: () => Promise<void>
    refreshMicrophoneLabel: () => Promise<void>
    refreshSubscriberState: () => Promise<void>
    refreshUserDeviceState: () => void
    upsertManyUserMetaData: (
      users: { userId: string; userName: string }[],
    ) => void
    upsertUserMetaData: (user: { userId: string; userName: string }) => void
  }
}

interface State extends UserVariables {
  userMetaDataMap: UserMetaDataMap
}

type Store = Actions & State

const initialState: State = {
  cameraLabel: '',
  company: '',
  country: '',
  displayName: '',
  email: '',
  firstName: '',
  fullBrowserVersion: '',
  gmtOffset: 0,
  hostname: window.location.hostname,
  isAndroid: false,
  isChrome: false,
  isChromium: false,
  isEdge: false,
  isFirefox: false,
  isIE: false,
  isIOS: false,
  isMobile: false,
  isOpera: false,
  isSafari: false,
  isTablet: false,
  isWinPhone: false,
  isYandex: false,
  lastName: '',
  memberId: '',
  microphoneLabel: '',
  name: '',
  osName: '',
  osVersion: '',
  timezone: '',
  tzString: '',
  userAgent: window.navigator.userAgent,
  userMetaDataMap: new Map(),
}

const stateCreatorFn: StateCreator<Store> = (set, get) => ({
  ...initialState,
  actions: {
    _getDisplayNameFallback: ({ email, firstName, lastName }) => {
      // Note: `displayName`, `firstName`, and `lastName` are not guaranteed to be present

      const firstNameTrimmed = firstName?.trim()
      const lastNameTrimmed = lastName?.trim()

      if (firstNameTrimmed || lastNameTrimmed) {
        return `${firstNameTrimmed} ${lastNameTrimmed}`.trim()
      }

      // fallback to email if no other name is available
      return email?.trim() || ''
    },
    getUserVariables: async ({ channel } = {}) => {
      const {
        refreshUserDeviceState,
        refreshSubscriberState,
        refreshCameraLabel,
        refreshMicrophoneLabel,
      } = get().actions

      refreshUserDeviceState()
      await refreshSubscriberState()

      if (channel === CHANNEL_TYPE.VIDEO) {
        await refreshCameraLabel()
      }
      await refreshMicrophoneLabel()

      return {
        cameraLabel: get().cameraLabel,
        company: get().company,
        country: get().country,
        displayName: get().displayName,
        email: get().email,
        firstName: get().firstName,
        fullBrowserVersion: get().fullBrowserVersion,
        gmtOffset: get().gmtOffset,
        hostname: get().hostname,
        isAndroid: get().isAndroid,
        isChrome: get().isChrome,
        isChromium: get().isChromium,
        isEdge: get().isEdge,
        isFirefox: get().isFirefox,
        isIE: get().isIE,
        isIOS: get().isIOS,
        isMobile: get().isMobile,
        isOpera: get().isOpera,
        isSafari: get().isSafari,
        isTablet: get().isTablet,
        isWinPhone: get().isWinPhone,
        isYandex: get().isYandex,
        lastName: get().lastName,
        memberId: get().memberId,
        microphoneLabel: get().microphoneLabel,
        name: get().name,
        osName: get().osName,
        osVersion: get().osVersion,
        timezone: get().timezone,
        tzString: get().tzString,
        userAgent: get().userAgent,
      } satisfies UserVariables
    },
    refreshCameraLabel: async () => {
      const { getSelectedCamera } = useDevicesStore.getState().actions
      const cameraLabel: string = (await getSelectedCamera())?.label || ''
      set({ cameraLabel })
    },
    refreshMicrophoneLabel: async () => {
      const { getSelectedMicrophone } = useDevicesStore.getState().actions
      const microphoneLabel: string =
        (await getSelectedMicrophone())?.label || ''
      set({ microphoneLabel })
    },
    refreshSubscriberState: async (): Promise<void> => {
      const { _getDisplayNameFallback } = get().actions
      const { client } = useMainStore.getState()
      const subscriber = await client?.getSubscriberInfo()
      if (subscriber) {
        const firstName = subscriber.first_name || ''
        const lastName = subscriber.last_name || ''
        const email = subscriber.email || ''
        const displayName =
          subscriber.display_name ||
          _getDisplayNameFallback({
            email,
            firstName,
            lastName,
          })

        set({
          company: subscriber.company_name || '',
          country: subscriber.country || '',
          displayName: displayName,
          email: email,
          firstName: firstName,
          lastName: lastName,
          memberId: subscriber.id,
          name: `${subscriber.first_name} ${subscriber.last_name}`,
        })
      }
    },
    refreshUserDeviceState: () => {
      const parser: UAParserInstance = new UAParser(window.navigator.userAgent)
      const {
        ua: userAgent,
        browser: { name: browserName, version: browserVersion },
        os: { name: osName, version: osVersion },
        device: { type: deviceType },
      }: IResult = parser.getResult()

      set({
        fullBrowserVersion: browserVersion || '',
        gmtOffset: (-1.0 * new Date().getTimezoneOffset()) / 60,
        hostname: window.location.hostname,
        isAndroid: osName === 'Android',
        isChrome: browserName === 'Chrome',
        isChromium: browserName === 'Chromium',
        isEdge: browserName === 'Edge',
        isFirefox: browserName === 'Firefox',
        isIE: browserName === 'IE',
        isIOS: osName === 'iOS',
        isMobile: deviceType === 'mobile',
        isOpera: browserName === 'Opera',
        isSafari: browserName === 'Safari',
        isTablet: deviceType === 'tablet',
        isWinPhone: osName === 'Windows Mobile',
        isYandex: browserName === 'Yandex',
        osName: osName || '',
        osVersion: osVersion || '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        tzString: new Date().toTimeString().split(' ').splice(1).join(' '),
        userAgent: userAgent,
      })
    },
    upsertManyUserMetaData: (users: { userId: string; userName: string }[]) => {
      const { memberId: selfMemberId, userMetaDataMap } = get()

      const userRecord = users.map(({ userId, userName }) => {
        return {
          id: userId,
          initial: userName.trim().charAt(0).toUpperCase() || 'U',
          name: userName.trim() || 'Unknown',
        }
      })

      // map only the new or updated user meta-data
      const newRecords = userRecord
        .map(userRecord => {
          const userMetaData = userMetaDataMap.get(userRecord.id)

          const isCurrentUser = userRecord.id === selfMemberId
          const getColor = isCurrentUser
            ? userToColor.getCurrentUserColor
            : () => userToColor.getUserColor(userRecord.id)

          if (!userMetaData) {
            // new user , create a new record
            return {
              ...userRecord,
              color: getColor(),
            }
          }
          // compare with existing user meta-data
          if (
            userRecord.initial !== userMetaData.initial ||
            userRecord.name !== userMetaData.name
          ) {
            // get a color to assign to the user if not already assigned
            const userColor = userMetaData.color || getColor()
            return {
              ...userRecord,
              color: userColor,
            }
          } else {
            // no changes
            return
          }
        })
        .filter(v => v) as UserMetaDataRecord[]

      // if no new records, no need to update
      if (newRecords.length === 0) {
        return
      }

      // clone and apply the new user meta-data
      const updatedMap = new Map(userMetaDataMap)

      newRecords.forEach(userRecord => {
        updatedMap.set(userRecord.id, userRecord)
      })

      set({ userMetaDataMap: updatedMap })
    },
    upsertUserMetaData: ({ userId, userName }) => {
      const { upsertManyUserMetaData } = get().actions
      upsertManyUserMetaData([{ userId, userName }])
    },
  },
})

export const useUserStore = create<Store>()(stateCreatorFn)
export const useUserStoreActions = () => useUserStore.getState().actions

export type { Store as UserStoreState }
